import { Box, Grid, Tooltip } from '@mui/material'
import { useFormContext } from 'react-hook-form'

import { ControlledCheckbox, ControlledTextField } from 'components/common'
import {
  ControlledSelector,
  ControlledTelephoneNumWithCountryCode,
  ISelectorOption,
} from 'components/common/controller-wrapper'
import { Roles } from 'constants/index'
import { useGetDriverQuery } from 'generated/graphql'
import { useUserRole } from 'hooks/useUserRole'

const RoleValues: ISelectorOption[] = [
  { value: Roles.driverSubcontractor, label: Roles.driverSubcontractor },
  { value: Roles.transportController, label: Roles.transportController },
]

interface IProps {
  disableRoleSelection?: boolean
  deactivatedByAdmin?: boolean
  primaryDriverId?: string
}

const SubDriverForm = (props: IProps) => {
  const { disableRoleSelection, deactivatedByAdmin, primaryDriverId } = props
  const spacing = 3
  const currentUserRole = useUserRole()
  const userIsNotAdmin = currentUserRole !== Roles.admin

  const { data: driverData } = useGetDriverQuery({
      variables: { driverId: Number(primaryDriverId) },
      skip: !primaryDriverId,
    })
  const primaryDriverDeactivatedByAdmin = driverData?.driver?.deactivatedByAdmin ?? undefined

  const {
    control,
    formState: { errors },
  } = useFormContext()

  const getTooltipTitle = () => {
    if (userIsNotAdmin && deactivatedByAdmin) {
      return 'This driver has been deactivated by an admin. Contact the administration for further assistance.'
    }

    if (primaryDriverDeactivatedByAdmin) {
      return 'The primary driver of this sub-driver has been deactivated by an admin.'
    }

    return ''
  }

  return (
    <Box sx={{ marginTop: 1 }}>
      <Grid container spacing={spacing}>
        <Grid item xs={12} sm={6}>
          <ControlledTextField
            control={control}
            label='First Name'
            required
            name='firstName'
            error={!!errors.firstName}
            helperText={errors.firstName?.message}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledTextField
            control={control}
            label='Last Name'
            required
            name='lastName'
            error={!!errors.lastName?.message}
            helperText={errors.lastName?.message}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <ControlledTextField
            control={control}
            name='email'
            label='Email'
            type='email'
            required
            error={!!errors.email}
            helperText={errors.email?.message}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledTelephoneNumWithCountryCode
            control={control}
            label='Telephone Number'
            name='telephoneNumber'
            required
            type='tel'
            fullWidth
            error={!!errors.telephoneNumber}
            helperText={errors.telephoneNumber?.message}
            hideEndAdornment={true}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Tooltip
            title={getTooltipTitle()}
            arrow>
            <span>
              <ControlledCheckbox
                control={control}
                label='Active'
                name='isActive'
                disabled={(userIsNotAdmin && deactivatedByAdmin) || primaryDriverDeactivatedByAdmin}
              />
            </span>
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledSelector
            options={RoleValues}
            name='role'
            label='Role'
            control={control}
            disabled={disableRoleSelection}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

export default SubDriverForm
